import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import { Icon } from '@heyroot/ui/primitives/Icon';
import { cn } from './cn';

// Root wrapper instead of a bare re-export: Radix renders a hidden native
// <select> (bubble input) when the Select sits inside a <form>. Setting the
// controlled value to an id whose <SelectItem> mounts in the SAME commit
// leaves the native select without a matching <option> for one commit; the
// browser keeps it at "" and Radix's autofill-forwarding change handler then
// calls onValueChange("") — silently clobbering the just-set value. Items can
// never carry value="" (check-radix-empty-select gate), so a real user
// selection can never legitimately emit "" — drop those callbacks.
// Repro: POS item drawer, "+ New department" auto-select (2026-06-10).
export function Select({
  onValueChange,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Root>) {
  return (
    <SelectPrimitive.Root
      {...(onValueChange
        ? {
            onValueChange: (value: string) => {
              if (value !== '') onValueChange(value);
            },
          }
        : {})}
      {...props}
    />
  );
}
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export function SelectTrigger({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        'flex h-[var(--h-field)] w-full items-center justify-between rounded-control',
        'border border-border bg-surface px-[var(--field-pad)] text-body-sm text-primary',
        'focus:outline-none focus:border-brand-primary focus:ring-[3px] focus:ring-ring-focus/20',
        'disabled:cursor-not-allowed disabled:opacity-[var(--disabled-opacity)]',
        'data-[placeholder]:text-muted',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon className="text-muted shrink-0">
        <Icon as={ChevronDown} size="xs" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        position={position}
        className={cn(
          // z-popover, not z-dropdown: the listbox portals to <body>, and Selects
          // open from inside drawers/dialogs (z-modal+1) — at z-dropdown (100) the
          // open listbox rendered BEHIND the drawer panel and looked dead.
          // Known residual (reversed case): a modal mounting WHILE a listbox is
          // open would layer below it (300 < 400) — unreachable today because
          // Radix closes the listbox on focus loss; don't add controlled-open
          // Selects.
          'relative z-[var(--z-popover)] min-w-[8rem] overflow-hidden rounded-control',
          'border border-border bg-surface-elevated shadow-md',
          'animate-overlay-fade-in',
          className,
        )}
        // Radix repositions the popover to avoid viewport collisions (e.g.
        // flipping open upward near the bottom of the page) but does NOT clamp
        // the content's own height to the space that repositioning left it --
        // a long option list can still render past the opposite viewport edge
        // with no way to scroll to the clipped items (repro: tenant-detail's
        // ~35-entry module picker, opened near the page bottom, hid every item
        // above the fold with no scrollbar). Bind to Radix's own
        // `--radix-select-content-available-height` var so the content never
        // exceeds its real available space, and add scroll buttons so an
        // overflowing list stays reachable instead of merely non-clipped.
        {...props}
        // Spread AFTER props (not before): a caller-supplied `style` prop would
        // otherwise silently clobber this maxHeight fix via object-spread
        // override order, reintroducing the exact clipping bug this fixes with
        // no visible trace of why. Merging `props.style` on top keeps a
        // deliberate caller override possible while never losing this by accident.
        style={{ maxHeight: 'var(--radix-select-content-available-height)', ...props.style }}
      >
        <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1">
          <Icon as={ChevronDown} size="xs" className="rotate-180 text-muted" />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport className="p-1">{children}</SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1">
          <Icon as={ChevronDown} size="xs" className="text-muted" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function SelectItem({
  className,
  children,
  // Rendered as a SIBLING of ItemText (never wrapped inside it) -- Radix
  // portals ONLY ItemText's own children into the collapsed trigger's
  // SelectValue when this item is selected, so anything that must appear in
  // the option list but NOT get echoed into the trigger (e.g. a status
  // badge) belongs here instead of in `children`. See ShiftDayNavigator's
  // topSelector usage (2026-07-28 fix) for the motivating case.
  endAdornment,
  // Additive/opt-in: appended to ItemText's own className. Left undefined,
  // ItemText renders exactly as before for every existing SelectItem call
  // site -- only set this when a specific item's text genuinely needs its
  // own truncation/flex behavior (see ShiftDayNavigator's topSelector).
  itemTextClassName,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  endAdornment?: ReactNode;
  itemTextClassName?: string;
}) {
  return (
    <SelectPrimitive.Item
      className={cn(
        'relative flex w-full cursor-default select-none items-center gap-2 rounded-control px-3 py-1.5',
        'text-body-sm text-primary outline-none',
        'focus:bg-surface-sunken',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-[var(--disabled-opacity)]',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className={itemTextClassName}>{children}</SelectPrimitive.ItemText>
      {endAdornment}
    </SelectPrimitive.Item>
  );
}

export const SelectLabel = SelectPrimitive.Label;
export const SelectSeparator = SelectPrimitive.Separator;
